// ثبت بچ (US-INV-030؛ AC-INV-003.1): اسکن GS1 DataMatrix (AI 10 بچ، 17 انقضا، 11 تولید) فیلدها را پر می‌کند؛ GTIN ناهمخوان با کالا هشدار می‌دهد؛
// انقضا برای کالای با ردیابی انقضا الزامی؛ شماره تکراری همان کالا ← PHX-INV-0111 (AC-INV-030.2). فاز 1 بدون مقدار و موجودی.
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type InvItemView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, FormDialog, JalaliDatePickerRHF, SelectRHF, TextFieldRHF } from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { parseGs1 } from './gs1';
import { inventoryKeys, useSupplierOptions } from './inventoryCommon';

const batchSchema = z.object({
  batchNumber: z.string().trim().min(1, { error: 'common:validation.required' }).max(40, { error: 'common:validation.too_long' }),
  expiryDate: z.string().nullable(),
  manufactureDate: z.string().nullable(),
  supplierId: z.string(),
  note: z.string().max(500, { error: 'common:validation.too_long' }),
});
type BatchForm = z.input<typeof batchSchema>;

export interface BatchDialogProps {
  item: InvItemView;
  /** متن اسکن ورودی (از صفحه فهرست). */
  initialScan?: string;
  onClose: () => void;
}

export function BatchDialog({ item, initialScan, onClose }: BatchDialogProps) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const suppliers = useSupplierOptions();
  const [scan, setScan] = useState(initialScan ?? '');
  const [scanned, setScanned] = useState(false);
  const [scanNotice, setScanNotice] = useState<{ severity: 'success' | 'warning' | 'error'; key: string; values?: Record<string, string> } | null>(null);
  const [submitError, setSubmitError] = useState<unknown>();
  const schema = batchSchema.superRefine((value, ctx) => {
    if (item.tracksExpiry && !value.expiryDate) {
      ctx.addIssue({ code: 'custom', path: ['expiryDate'], message: 'inv:batch_form.expiry_required' });
    }
  });
  const { control, handleSubmit, formState, setError, setValue } = useForm<BatchForm>({
    resolver: zodResolver(schema),
    defaultValues: { batchNumber: '', expiryDate: null, manufactureDate: null, supplierId: '', note: '' },
  });

  const applyScan = (raw: string) => {
    const result = parseGs1(raw);
    if (!result.ok) {
      setScanNotice({ severity: 'error', key: 'inv:batch_form.scan_invalid', values: { reason: t(`inv:scan_reason.${result.reason}`) } });
      return;
    }
    const { data } = result;
    if (data.batchNumber) {
      setValue('batchNumber', data.batchNumber, { shouldDirty: true, shouldValidate: true });
    }
    if (data.expiryDate) {
      setValue('expiryDate', data.expiryDate, { shouldDirty: true, shouldValidate: true });
    }
    if (data.productionDate) {
      setValue('manufactureDate', data.productionDate, { shouldDirty: true });
    }
    setScanned(true);
    setScanNotice(
      data.gtin && item.gtin && data.gtin !== item.gtin
        ? { severity: 'warning', key: 'inv:batch_form.scan_gtin_mismatch', values: { gtin: data.gtin } }
        : { severity: 'success', key: data.serialNumber ? 'inv:batch_form.scan_parsed_serial' : 'inv:batch_form.scan_parsed', values: { serial: data.serialNumber ?? '' } },
    );
  };

  useEffect(() => {
    if (initialScan) {
      applyScan(initialScan);
    }
    // فقط نخستین باز شدن با متن اسکن.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScanKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyScan(scan);
    }
  };

  const save = useMutation({
    mutationFn: (form: BatchForm) =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/items/{id}/batches', {
          params: { path: { id: item.id } },
          body: {
            batchNumber: form.batchNumber.trim(),
            expiryDate: form.expiryDate,
            manufactureDate: form.manufactureDate,
            supplierId: form.supplierId || null,
            note: form.note.trim() || null,
            source: scanned ? 'Scan' : 'Manual',
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.batches(session?.tenantId, item.id) });
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.item(session?.tenantId, item.id) });
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.items(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['batchNumber', 'expiryDate', 'manufactureDate', 'supplierId', 'note'],
        codeFields: { 'PHX-INV-0111': 'batchNumber', 'PHX-INV-0110': 'supplierId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-870-BATCH-FORM"
      titleKey="inv:batch_form.create_title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <TextField
        fullWidth
        autoFocus
        label={t('inv:batch_form.scan_label')}
        helperText={t('inv:batch_form.scan_help')}
        value={scan}
        onChange={(event) => setScan(event.target.value)}
        onKeyDown={onScanKey}
        slotProps={{
          input: { startAdornment: <InputAdornment position="start"><QrCodeScannerIcon /></InputAdornment> },
          htmlInput: { dir: 'ltr', 'data-testid': 'batch-scan' },
        }}
      />
      {scanNotice && (
        <Alert severity={scanNotice.severity} data-testid="batch-scan-notice" data-severity={scanNotice.severity}>
          {t(scanNotice.key, scanNotice.values)}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="batchNumber" labelKey="inv:batch_form.batch_number" required fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'batch-number' } }} />
        <JalaliDatePickerRHF control={control} name="expiryDate" labelKey="inv:batch_form.expiry" required={item.tracksExpiry} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={control} name="manufactureDate" labelKey="inv:batch_form.manufacture" />
        {access.can('inv.supplier.manage') && (
          <SelectRHF
            control={control}
            name="supplierId"
            labelKey="inv:batch_form.supplier"
            fullWidth
            options={[
              { value: '', labelKey: 'inv:batch_form.no_supplier' },
              ...(suppliers.data?.items ?? []).map((supplier) => ({ value: supplier.id, label: supplier.name })),
            ]}
          />
        )}
      </Stack>
      <TextFieldRHF control={control} name="note" labelKey="inv:batch_form.note" fullWidth />
    </FormDialog>
  );
}
